// Planner ガント印刷ユーティリティ
// - 一括で「タイトル + メタ + QRコード + ガント本体 + 任意のリスト表」を A4横印刷用HTMLにまとめる。
// - QRコードは公開共有リンク（/g/{token}）を指し、ログイン不要で誰でも閲覧できる。
// - ProjectBoard と DepartmentDashboard の双方から呼び出される。
import QRCode from 'qrcode';
import { openPrintWindow } from '@/lib/printWindow';
import { plannerApi } from './plannerApi';
import { ganttOrder } from './plannerConstants';

const esc = (s = '') => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

// 公開共有リンク＋QRコードを取得（未発行なら自動発行）。失敗しても印刷は継続する。
export async function fetchShareQr(kind, targetId) {
  try {
    let s = await plannerApi.getShare(kind, targetId);
    if (!s?.token) s = await plannerApi.createShare(kind, targetId);
    if (!s?.token) return { url: '', dataUrl: '' };
    const url = `${window.location.origin}/g/${s.token}`;
    const dataUrl = await QRCode.toDataURL(url, { width: 320, margin: 1 });
    return { url, dataUrl };
  } catch (_e) {
    return { url: '', dataUrl: '' };
  }
}

// ガント本体を A4横向け HTML として組み立てる（ProjectBoard の実装を踏襲）。
// 端に近いバーは日付ラベルを右寄せに切替えて枠外にはみ出さないようにする。
function buildGanttHtml(items, opts = {}) {
  const { emptyText = 'タスクがありません', itemLabel = '項目' } = opts;
  const toD = (s) => { if (!s) return null; const d = new Date(String(s).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? null : d; };
  const addD = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const diffD = (a, b) => Math.round((a - b) / 86400000);
  // 画面のガントと同じ並び（優先度 → 終了日の早い順 → 開始日）に統一する。
  const sorted = [...items].sort(ganttOrder((it) => it.end || it.start, (it) => it.start || it.end));
  const rows = sorted.map((it) => {
    let s = toD(it.start), e = toD(it.end);
    if (!s && e) s = e; if (!e && s) e = s;
    return { ...it, s, e };
  }).filter((r) => r.s && r.e);
  if (rows.length === 0) return `<p class="meta">${esc(emptyText)}</p>`;
  const all = rows.flatMap((r) => [r.s, r.e]);
  const min = addD(new Date(Math.min(...all)), -1);
  const max = addD(new Date(Math.max(...all)), 1);
  const totalDays = diffD(max, min) + 1;
  const dayPct = 100 / totalDays;
  const days = Array.from({ length: totalDays }, (_, i) => addD(min, i));
  const months = [];
  days.forEach((d) => {
    const key = `${d.getFullYear()}/${d.getMonth() + 1}`;
    const last = months[months.length - 1];
    if (!last || last.key !== key) months.push({ key, count: 1 }); else last.count++;
  });
  const HEAD_MM = 6;
  const PAGE_BODY_MM = 155 - HEAD_MM - 2;
  const MIN_ROW_MM = 4.5;
  const MAX_ROW_MM = 7.0;
  const PREF_ROW_MM = 6.0;
  const maxRowsSinglePage = Math.floor(PAGE_BODY_MM / MIN_ROW_MM);
  let rowMm, pages;
  if (rows.length <= maxRowsSinglePage) {
    rowMm = Math.max(MIN_ROW_MM, Math.min(MAX_ROW_MM, PAGE_BODY_MM / rows.length));
    pages = [rows];
  } else {
    rowMm = PREF_ROW_MM;
    const rowsPerPage = Math.max(1, Math.floor(PAGE_BODY_MM / rowMm));
    pages = [];
    for (let i = 0; i < rows.length; i += rowsPerPage) pages.push(rows.slice(i, i + rowsPerPage));
  }
  const barTopMm = (rowMm * 0.48).toFixed(2);
  const barHMm = Math.max(2.0, rowMm * 0.42).toFixed(2);
  const dateFontMm = Math.max(1.6, Math.min(2.5, rowMm * 0.4)).toFixed(2);
  const rowMmStr = rowMm.toFixed(2);
  const monthCells = months.map((m) => {
    const w = (m.count * dayPct).toFixed(3);
    const showLabel = m.count >= 3;
    return `<div class="g-month" style="width:${w}%;line-height:${HEAD_MM}mm">${showLabel ? m.key : ''}</div>`;
  }).join('');
  const fmtMD = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayInRange = today >= min && today <= max;
  const todayLeftPct = todayInRange ? ((diffD(today, min) + 0.5) * dayPct).toFixed(3) : null;
  const todayOverlay = todayInRange
    ? `<div class="g-today-overlay"><div class="g-today-line" style="left:${todayLeftPct}%"></div><div class="g-today-lbl" style="left:${todayLeftPct}%">今日</div></div>`
    : '';
  const renderPage = (pageRows, pageIdx, totalPages) => {
    const bodyRows = pageRows.map((r) => {
      const leftPct = diffD(r.s, min) * dayPct;
      const wPct = Math.max(diffD(r.e, r.s) + 1, 1) * dayPct;
      const left = leftPct.toFixed(3);
      const w = wPct.toFixed(3);
      const range = r.s.getTime() === r.e.getTime() ? fmtMD(r.s) : `${fmtMD(r.s)}〜${fmtMD(r.e)}`;
      const estLabelWidthPct = range.length * parseFloat(dateFontMm) * 0.7 / 200 * 100;
      const anchorRight = leftPct + estLabelWidthPct > 100;
      const labelStyle = anchorRight
        ? `right:${Math.max(0, 100 - leftPct - wPct).toFixed(3)}%;text-align:right`
        : `left:${left}%;text-align:left`;
      const c = r.color || '#4F46E5';
      return `<div class="g-row" style="height:${rowMmStr}mm"><div class="g-label" style="line-height:${rowMmStr}mm">${esc(r.label)}</div><div class="g-track" style="height:${rowMmStr}mm"><div class="g-daterange" style="${labelStyle};font-size:${dateFontMm}mm">${esc(range)}</div><div class="g-bar" style="left:${left}%;width:${w}%;top:${barTopMm}mm;height:${barHMm}mm;background:${c};background-image:linear-gradient(${c},${c})"></div></div></div>`;
    }).join('');
    const pageBadge = totalPages > 1 ? `<div class="g-page-lbl">${pageIdx + 1} / ${totalPages}</div>` : '';
    return `<div class="gantt"><div class="g-row g-head" style="height:${HEAD_MM}mm"><div class="g-label" style="line-height:${HEAD_MM}mm">${esc(itemLabel)}</div><div class="g-track" style="display:flex;height:${HEAD_MM}mm">${monthCells}</div></div>${bodyRows}${todayOverlay}${pageBadge}</div>`;
  };
  return pages.map((pg, i) => (i > 0 ? '<div class="pb"></div>' : '') + renderPage(pg, i, pages.length)).join('');
}

const BASE_STYLE = `
  @page{size:A4 landscape;margin:10mm}
  *{box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; color-adjust:exact}
  body{font-family:'Yu Gothic','Hiragino Sans',sans-serif;color:#1f2937;margin:0}
  .hdr{display:flex;align-items:flex-start;justify-content:space-between;gap:8mm;margin:0 0 6px}
  .hdr .ttl{min-width:0;flex:1}
  h1{font-size:18px;margin:0 0 4px}
  h2{font-size:13px;margin:0 0 8px;color:#374151}
  p.meta{color:#6b7280;font-size:12px;margin:0 0 14px}
  .qr-box{flex:0 0 auto;text-align:center;border:1px solid #e4e4e7;border-radius:4px;padding:1.5mm 2mm;background:#fff}
  .qr-img{width:20mm;height:20mm;display:block}
  .qr-cap{font-size:7px;color:#6b7280;margin-top:0.8mm;letter-spacing:0.02em}
  .qr-url{font-size:5.5px;color:#3f3f46;margin-top:0.5mm;word-break:break-all;max-width:24mm;line-height:1.35}
  table{border-collapse:collapse;width:100%;font-size:11.5px;table-layout:fixed}
  thead{display:table-header-group}
  tr{page-break-inside:avoid;break-inside:avoid}
  th,td{border:1px solid #ddd;padding:5px 8px;text-align:left;vertical-align:top;word-break:break-word;overflow-wrap:anywhere;page-break-inside:avoid;break-inside:avoid}
  th{background:#f4f4f5;background-image:linear-gradient(#f4f4f5,#f4f4f5);font-weight:600}
  .td-mem{font-size:11px;color:#3f3f46}
  .td-notes{font-size:11px;color:#3f3f46;white-space:pre-wrap}
  .td-date{white-space:nowrap;font-size:11px}
  .gantt{border:1px solid #e4e4e7;border-radius:6px;overflow:hidden;margin-bottom:6px;position:relative;page-break-inside:avoid;break-inside:avoid}
  .g-row{display:flex;border-bottom:1px solid #f1f1f3;page-break-inside:avoid}
  .g-row:last-child{border-bottom:none}
  .g-head{background:#f4f4f5;background-image:linear-gradient(#f4f4f5,#f4f4f5);font-weight:600;color:#52525b;font-size:9px}
  .g-label{width:48mm;flex:0 0 48mm;padding:0 6px;border-right:1px solid #e4e4e7;font-size:9px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
  .g-track{position:relative;flex:1 1 auto}
  .g-month{border-right:1px solid #e4e4e7;font-size:8px;padding:0 3px;color:#52525b;overflow:hidden;white-space:nowrap;background:#f4f4f5;background-image:linear-gradient(#f4f4f5,#f4f4f5)}
  .g-daterange{position:absolute;top:0.4mm;line-height:1;color:#3f3f46;white-space:nowrap;padding:0 1px;font-weight:600}
  .g-bar{position:absolute;border-radius:3px;border:0.5px solid rgba(0,0,0,0.15)}
  .g-today-overlay{position:absolute;top:0;bottom:0;left:48mm;right:0;pointer-events:none;z-index:20}
  .g-today-line{position:absolute;top:0;bottom:0;width:0.4mm;background:#e11d48;background-image:linear-gradient(#e11d48,#e11d48);z-index:20;pointer-events:none}
  .g-today-lbl{position:absolute;top:0;transform:translateX(-50%);background:#e11d48;background-image:linear-gradient(#e11d48,#e11d48);color:#fff;font-size:6.5px;font-weight:700;padding:0.4mm 1mm;border-radius:0 0 1mm 1mm;line-height:1;white-space:nowrap;z-index:21}
  .g-page-lbl{position:absolute;top:1mm;right:2mm;font-size:6.5px;font-weight:700;color:#52525b;background:#f4f4f5;background-image:linear-gradient(#f4f4f5,#f4f4f5);padding:0.4mm 1.5mm;border-radius:2mm;line-height:1;z-index:22;letter-spacing:0.02em}
  .pb{page-break-before:always;break-before:page}
  @media print {
    *{-webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; color-adjust:exact !important}
    .g-bar,.g-today-line,.g-today-lbl,.g-head,.g-month,th{-webkit-print-color-adjust:exact !important; print-color-adjust:exact !important}
  }
`;

// 呼び出し側は title / meta / items / (任意) listSection を渡すだけ。
// shareKind='project'|'department', shareId=対象ID を渡すと自動でQRコード発行→ヘッダ右上に埋め込む。
export async function printGanttChart({
  title,
  meta = '',
  ganttHeading = 'ガント',
  items = [],
  emptyText = 'タスクがありません',
  itemLabel = '項目',
  listSection = '',   // ex: '<h2>一覧</h2><table>...</table>' -- 別ページで表示
  shareKind,
  shareId,
}) {
  const { url, dataUrl } = shareKind && shareId ? await fetchShareQr(shareKind, shareId) : { url: '', dataUrl: '' };
  const shortUrl = url.replace(/^https?:\/\//, '');
  const qrBlock = dataUrl
    ? `<div class="qr-box"><img class="qr-img" src="${dataUrl}" alt="QR" /><div class="qr-cap">スキャンで閲覧</div><div class="qr-url">${esc(shortUrl)}</div></div>`
    : '';
  const ganttHtml = buildGanttHtml(items, { emptyText, itemLabel });
  const html = `<html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head><body>
    <div class="hdr">
      <div class="ttl">
        <h1>${esc(title)}</h1>
        ${meta ? `<p class="meta">${esc(meta)}</p>` : ''}
      </div>
      ${qrBlock}
    </div>
    <h2>${esc(ganttHeading)}</h2>
    ${ganttHtml}
    ${listSection ? `<div class="pb"></div>${listSection}` : ''}
  </body></html>`;
  return openPrintWindow(html, { delay: 500 });
}

// ProjectBoard の 「タスク一覧」表を組み立てる補助関数（QRブロックとは独立）。
// withNotes=true のときだけ備考列を追加（空の備考列はレイアウト崩れの原因になる）。
export function buildTaskListSection({ heading, headerLabels, rowsHtml, withNotes = false }) {
  const heads = headerLabels.map((h) => `<th>${esc(h)}</th>`).join('');
  const cols = withNotes
    ? '<col style="width:24%"/><col style="width:7%"/><col style="width:7%"/><col style="width:18%"/><col style="width:18%"/><col style="width:26%"/>'
    : '<col style="width:34%"/><col style="width:8%"/><col style="width:8%"/><col style="width:26%"/><col style="width:24%"/>';
  return `<h2>${esc(heading)}</h2><table><colgroup>${cols}</colgroup><thead><tr>${heads}</tr></thead><tbody>${rowsHtml}</tbody></table>`;
}

export { ganttOrder };
